import React, { useEffect, useRef } from 'react';
import { copilotConfig } from '@components/Copilot/constants';
import CopilotMessageFeedback from '@components/Copilot/CopilotMessageFeedback';
import DefaultPrompts from '@components/Copilot/DefaultPrompts';
import ShimmerText from '@components/Copilot/ShimmerText';
import { ChatbotErrorType, ChatMessage, CopilotChatMessageType, TCopilotActions } from '@components/Copilot/types';
import { formatRelativeTime, getAiMagicIcon } from '@components/Copilot/utils';
import { useAvContext } from '@context/AvContextProvider';
import { useAvCopilotContext } from '@context/AvCopilotProvider';
import { AvButton, AvMarkdown, Initials } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { arrayEquals } from '@fabric-ui/utils';
import { Box, Divider } from '@mui/material';

interface ICopilotChatMessages {
  messages: ChatMessage[];
  isLoading?: boolean;
  onUserPromptHandler: (prompt: string) => void;
}

const errorButtonTextMap = {
  [ChatbotErrorType.NOT_FOUND]: 'Start New Session',
};

const CopilotChatMessages: React.FC<ICopilotChatMessages> = ({ messages, isLoading = false, onUserPromptHandler }) => {
  const { user } = useAvContext();
  const {
    restartCopilotSession,
    sessionId,
    conversationErrorMessage,
    lastSessionProps,
    isDefaultPromptsOpen,
    setIsDefaultPromptsOpen,
    showActionButtons,
    thinkingText,
  } = useAvCopilotContext();

  const lastElement = useRef<HTMLDivElement | null>(null);

  const onErrorHandler = () => restartCopilotSession();

  useEffect(() => {
    if (lastElement?.current) {
      lastElement.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isLoading, isDefaultPromptsOpen]);

  return (
    <Box
      sx={{
        position: 'relative',
        ...flex.col,
        gap: 4,
        pt: 3,
        width: '100%',
      }}>
      {messages.map((message, index) => {
        const showActions =
          !isLoading &&
          index === messages.length - 1 &&
          messages.length >= 2 &&
          !!lastSessionProps?.actions.length &&
          !conversationErrorMessage &&
          showActionButtons;
        return (
          <CopilotChatMessage
            key={message.timestamp}
            message={message.content}
            userName={user?.userName}
            timestamp={message.timestamp}
            type={message.type}
            state={message.state}
            lastMessageActions={showActions ? lastSessionProps.actions : []}
            sessionId={sessionId}
            loopId={message.loopId!}
          />
        );
      })}
      {isDefaultPromptsOpen && !isLoading && (
        <DefaultPrompts
          prompts={copilotConfig[lastSessionProps.agentId]?.defaultPrompts || []}
          onClick={v => {
            setIsDefaultPromptsOpen(false);
            onUserPromptHandler(v);
          }}
        />
      )}
      {isLoading && (
        <Box sx={{ ...flex.col, gap: 1 }}>
          <Box sx={{ ...flex.row, gap: 1 }}>
            {getAiMagicIcon(30)}
            <ShimmerText text={thinkingText} />
          </Box>
        </Box>
      )}
      {!isLoading && conversationErrorMessage && (
        <Box sx={{ ...flex.col, gap: 1 }}>
          {getAiMagicIcon(30)}
          <AvMarkdown content={conversationErrorMessage.message} sx={{ color: 'var(--color-content-base-primary)' }} />
          <AvButton
            size="xSmall"
            variant="outlined"
            aria-label={errorButtonTextMap[conversationErrorMessage.type] || 'Retry'}
            onClick={onErrorHandler}
            sx={{ width: 'fit-content' }}>
            {errorButtonTextMap[conversationErrorMessage.type] || 'Retry'}
          </AvButton>
        </Box>
      )}

      <Box ref={lastElement} sx={{ position: 'absolute', bottom: 0 }} />
    </Box>
  );
};

interface ICopilotChatMessage {
  message: string;
  userName: string;
  timestamp: string;
  type: CopilotChatMessageType;
  state?: any;
  lastMessageActions: TCopilotActions;
  sessionId: string;
  loopId: string;
}

const CopilotChatMessage: React.FC<ICopilotChatMessage> = ({
  message,
  userName,
  timestamp,
  type,
  state,
  lastMessageActions = [],
  sessionId,
  loopId,
}) => {
  const isAiRole = [CopilotChatMessageType.CONVERSATION_MESSAGE_TYPE_AI, CopilotChatMessageType.CONVERSATION_MESSAGE_TYPE_SUMMARY].includes(
    type
  );

  return (
    <Box sx={{ ...flex.col, gap: 1 }}>
      {isAiRole ? getAiMagicIcon(30) : <Initials name={userName || ''} size="small" />}
      <AvMarkdown content={message} sx={{ color: 'var(--color-content-base-primary)', whiteSpace: 'pre-wrap' }} />
      {isAiRole ? (
        <CopilotMessageFeedback
          formattedTimestamp={formatRelativeTime(timestamp)}
          message={message}
          loopId={loopId}
          sessionId={sessionId}
        />
      ) : (
        <Box sx={{ fontSize: 12, color: 'var(--color-content-base-tertiary)' }}>{formatRelativeTime(timestamp)}</Box>
      )}
      {!!lastMessageActions.length && isAiRole && (
        <Divider flexItem sx={{ width: '100%', borderColor: 'var(--color-border-base-secondary)' }} />
      )}
      {lastMessageActions.map(action => (
        <React.Fragment key={action.toString()}>{action({ content: message, type, timestamp, sessionId, state })}</React.Fragment>
      ))}
    </Box>
  );
};

const MemoizedCopilotChatMessages = React.memo(CopilotChatMessages, (oldProps, newProps) => {
  if (oldProps.messages.length !== newProps.messages.length) {
    return false;
  }
  if (oldProps.isLoading !== newProps.isLoading) {
    return false;
  }
  return arrayEquals(oldProps.messages, newProps.messages);
});

export default MemoizedCopilotChatMessages;
